import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  AbsenceFilesPlaceholderIcon,
  Box,
  CalendarIcon,
  CheckBox,
  ClearAll,
  DateRangeDropdown,
  FlexList,
  FlexListPlaceholder,
  NoSearchItemsPlaceholder,
  PaginationBar,
  Preloader,
  SearchInput,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { ALL_OPTION } from '@/common/constants';
import routes from '@/common/routes';
import { ListItemBase, ListItemType, OptionKey } from '@/common/types';
import { QueryKeys, useHistory } from '@/modules/core/hooks';
import {
  CLAIMS_STATUSES_OPTIONS,
  ClaimsStatus,
} from '@/modules/employer/components/Claims/types/claims.constants';
import { useListPagination } from '@/modules/employer/hooks';
import { PaymentType } from '@/modules/transaction/transaction.types';
import globalTheme from '@/styles/theme';
import theme from '@/styles/theme';
import { useFilters } from '@/utils';
import { formatDateRange } from '@/utils/modifiers';

import useClaimsList from './hooks/useClaimsList';
import AssignClaimsProcessorsPopup from './AssignClaimsProcessorsPopup';
import { PageMode } from './ClaimsFilterSection';
import EmptyClaimsPlaceholder from './EmptyClaimsPlaceholder';
import { CHECKED_CLASS_NAME } from './MoovingWrapper';

export enum SearchKeysForPageMode {
  search = 'search',
  paymentTypes = 'paymentTypes',
  statuses = 'statuses',
  dateRange = 'dateRange',
  page = 'page',
}
export const ACTIVE_PAGE_MODE_SEARCH_PARAM = 'activePageMode';

const DEFAULT_PER_PAGE = 20;
const DEFAULT_PAGE = 1;

const StyledAbsenceFilesPlaceholderIcon = styled(AbsenceFilesPlaceholderIcon)`
  fill: ${({ theme }) => theme.colors.border};
`;

const EXTENDED_HEADER_HEIGHT = 140;
const HEADER_HEIGHT = 144;
export const EXTENDED_HEADER_CLAIMS_SPACING = 24;
export const HEADER_CLAIMS_SPACING = 24;

const PAYMENT_TYPES = [
  { ...ALL_OPTION, id: '' },
  { id: '0', key: 'REIMBURSEMENT', value: 'Reimbursement' },
  { id: '1', key: 'BILL_PAY', value: 'Pay a Bill' },
  { id: '2', key: 'CARD_SWIPE', value: 'Card swipe' },
];

const CheckBoxWrapper = styled(Box)`
  input[type='checkbox'] + div,
  input[type='checkbox']:not(:checked) + div,
  input[type='checkbox']:not(:checked):focus + div {
    background-color: ${({ theme }) => theme.colors.legend};
    border-color: ${({ theme }) => theme.colors.border} !important;
    width: ${({ theme }) => theme.spacings.spacing16};
    height: ${({ theme }) => theme.spacings.spacing16};
  }
`;

const DEFAULT_STATUSES = [ClaimsStatus.Processing] as OptionKey[];
const APPEAL_STATUSES: OptionKey[] = [];

export interface EscalatedSharedFilters {
  statuses: OptionKey[];
  searchString: string;
  paymentType: OptionKey;
  dateRange: Array<Date | null>;
}

export const ESCALATED_DEFAULT_FILTERS: EscalatedSharedFilters = {
  statuses: DEFAULT_STATUSES,
  searchString: '',
  paymentType: ALL_OPTION.value as OptionKey,
  dateRange: [],
};

export const APPEALS_DEFAULT_FILTERS: EscalatedSharedFilters = {
  statuses: APPEAL_STATUSES,
  searchString: '',
  paymentType: ALL_OPTION.value as OptionKey,
  dateRange: [],
};

const statusAllToggling = (statuses?: OptionKey[]) => {
  const isStatusAllSelected = statuses?.[statuses.length - 1] === ClaimsStatus.Null;

  if (statuses?.length === 0 || isStatusAllSelected) {
    return [ClaimsStatus.Null];
  }

  const isStatusAllChangedToAnother = statuses?.length === 2 && statuses?.[0] === ClaimsStatus.Null;

  if (isStatusAllChangedToAnother) {
    return statuses.filter((status) => status !== ClaimsStatus.Null);
  }

  return statuses;
};

interface Props {
  setTotal: (val: number | undefined) => void;
  isOnlyMyAssignments?: boolean;
  isShowOnlyEscalated?: boolean;
  isAppealed?: boolean;
  isAppealProcessed?: boolean;
  hasAccessToAssignClaim?: boolean;
  isClockIn?: boolean;
  hasAccessToClockIn?: boolean;
  pageMode: PageMode;
  disabled?: boolean;
  queryEnabled?: boolean;
  subTabsNode?: React.ReactNode;
  sharedFilters?: EscalatedSharedFilters;
  onSharedFiltersChange?: (filters: EscalatedSharedFilters) => void;
}

const ClaimsList: React.FC<Props> = ({
  isOnlyMyAssignments,
  isShowOnlyEscalated,
  isAppealed,
  isAppealProcessed,
  hasAccessToAssignClaim,
  isClockIn,
  hasAccessToClockIn,
  setTotal,
  pageMode,
  disabled,
  queryEnabled = true,
  subTabsNode,
  sharedFilters,
  onSharedFiltersChange,
}) => {
  const listTestId = pageMode === PageMode.ESCALATED && isAppealed === true
    ? 'claims_list_escalated_appeals'
    : pageMode === PageMode.ESCALATED && isAppealed === false
      ? 'claims_list_escalated_claims'
      : `claims_list_${pageMode.toLowerCase()}`;
  const initialStatuses = (isAppealed && !isShowOnlyEscalated) ? APPEAL_STATUSES : DEFAULT_STATUSES;
  const [initCleaning, setInitCleaning] = useState(false);
  const { page, perPage, setPerPage, setPage } = useListPagination();
  const [searchKey, paymentTypesKey, statusesKey, dateRangeKey, pageKey] = useMemo(
    () => [
      `${SearchKeysForPageMode.search}${pageMode}`,
      `${SearchKeysForPageMode.paymentTypes}${pageMode}`,
      `${SearchKeysForPageMode.statuses}${pageMode}`,
      `${SearchKeysForPageMode.dateRange}${pageMode}`,
      `${SearchKeysForPageMode.page}${pageMode}`,
    ],
    [pageMode]
  );
  const { filtersValues, setFilterValue } = useFilters<{
    [searchKey]: string;
    [paymentTypesKey]: OptionKey;
    [statusesKey]: OptionKey[];
    [dateRangeKey]: Array<Date | null>;
    [ACTIVE_PAGE_MODE_SEARCH_PARAM]: string;
    [pageKey]: string;
  }>(
    {
      [searchKey]: '',
      [paymentTypesKey]: ALL_OPTION.value,
      [statusesKey]: initialStatuses,
      [dateRangeKey]: [],
      [ACTIVE_PAGE_MODE_SEARCH_PARAM]: '',
      [pageKey]: '',
    },
    {
      autoClear: false,
      disabled: disabled || !!sharedFilters,
    }
  );

  const [isPageHydratedFromUrl, setIsPageHydratedFromUrl] = useState(false);
  useEffect(() => {
    if (!isPageHydratedFromUrl) {
      return;
    }
    if (!disabled && !sharedFilters) {
      setFilterValue?.[pageKey]?.(String(page));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isPageHydratedFromUrl]);
  const [searchString, paymentTypes, statuses, dateRange] = useMemo(
    () => sharedFilters
      ? [sharedFilters.searchString, sharedFilters.paymentType, sharedFilters.statuses, sharedFilters.dateRange]
      : [
          filtersValues[searchKey] as string,
          filtersValues[paymentTypesKey] as OptionKey,
          filtersValues[statusesKey] as OptionKey[],
          filtersValues[dateRangeKey] as Array<Date | null>,
        ],
    [sharedFilters, filtersValues, searchKey, paymentTypesKey, statusesKey, dateRangeKey]
  );

  useEffect(() => {
    if (!disabled) {
      if (!sharedFilters) {
        const searchParams = new URLSearchParams(location.search);
        const searchedStatuses = searchParams?.get(`statuses${pageMode}`)?.split(',');
        const hasVisitedPageMode = searchParams?.get(ACTIVE_PAGE_MODE_SEARCH_PARAM) === pageMode;
        const fallbackStatuses = hasVisitedPageMode ? [] : initialStatuses;
        setFilterValue?.[statusesKey]?.(searchedStatuses?.length ? searchedStatuses : fallbackStatuses);
      }
      setFilterValue?.[ACTIVE_PAGE_MODE_SEARCH_PARAM](pageMode);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [disabled]);

  useEffect(() => {
    setPerPage(DEFAULT_PER_PAGE);
    const urlPage = Number(new URLSearchParams(window.location.search).get(pageKey));
    setPage(urlPage > 0 ? urlPage : DEFAULT_PAGE);
    setIsPageHydratedFromUrl(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchingPaymentType = useMemo(
    () => PAYMENT_TYPES.find((type) => type.key === paymentTypes)?.key,
    [paymentTypes]
  );

  const hasPaymentType = useMemo(() => paymentTypes !== ALL_OPTION.value, [paymentTypes]);
  const statusValues = useMemo(() => statusAllToggling(statuses), [statuses]);
  const hasStatus = useMemo(
    () =>
      statusValues?.length &&
      statusValues?.length > 0 &&
      !statusValues.some((status) => status === ClaimsStatus.Null),
    [statusValues]
  );
  const hasDateRange = useMemo(() => Boolean(dateRange && dateRange?.[0] && dateRange?.[1]), [dateRange]);

  const [selectedClaimsIds, setSelectedClaimsIds] = useState<string[]>([]);
  useEffect(() => setSelectedClaimsIds([]), [pageMode, setSelectedClaimsIds]);
  const hasClaimsTeamColumn = useMemo(
    () => !isOnlyMyAssignments && Boolean(selectedClaimsIds.length),
    [isOnlyMyAssignments, selectedClaimsIds]
  );
  const {
    data: claims,
    total,
    isLoading,
    list,
    isRefetching,
  } = useClaimsList({
    page: page - 1,
    perPage,
    hasAccessToAssignClaim,
    ...(searchString ? { searchString: searchString } : {}),
    ...(hasPaymentType ? { paymentType: searchingPaymentType as PaymentType } : {}),
    statuses: [statusValues || initialStatuses].flat()?.filter((status) => status !== ALL_OPTION.value),
    ...(hasDateRange ? { dateRange: formatDateRange(dateRange) } : {}),
    isOnlyMyAssignments,
    isShowOnlyEscalated,
    isAppealed,
    isAppealProcessed,
    enabled: queryEnabled,
    isClockIn,
    hasAccessToClockIn,
    selectedClaimsIds,
    setSelectedClaimsIds,
    hasClaimsTeamColumn,
  });
  useEffect(() => {
    setTotal(total);
  }, [total, setTotal]);
  const [areAllClaimsSelected, selectAllClaims] = useState(false);
  const selectedClaims = useMemo(
    () => list?.filter((claim) => selectedClaimsIds.some((id) => claim?.id === id)),
    [list, selectedClaimsIds]
  );
  useEffect(() => {
    if (claims?.length && claims.length === selectedClaimsIds.length) {
      selectAllClaims(true);
    } else if (isLoading && selectedClaimsIds.length) {
      selectAllClaims(false);
      setSelectedClaimsIds([]);
    } else if (selectedClaimsIds.length === 0) {
      selectAllClaims(false);
    }
  }, [claims, selectedClaimsIds, isLoading]);
  const actualClaims = useMemo(() => {
    if (hasAccessToAssignClaim) {
      return claims.map((claim) => {
        const isSelected = selectedClaimsIds.some((item) => item === claim.id);

        return {
          ...claim,

          fields: [
            {
              key: 'selectClaim',
              title: 'select claim',
              type: ListItemType.Node,
              flex: '0 1 40px',
              node: (
                <Box {...(isSelected ? { className: CHECKED_CLASS_NAME } : {})}>
                  <CheckBox
                    onChange={() => {
                      if (isSelected) {
                        setSelectedClaimsIds([...selectedClaimsIds.filter((item) => item !== claim.id)]);
                        selectAllClaims(false);
                      } else {
                        setSelectedClaimsIds([...selectedClaimsIds, claim.id]);
                      }
                    }}
                    checked={isSelected}
                  />
                </Box>
              ),
            },
            ...claim.fields,
          ],

          ...(isSelected
            ? { rowStyle: { outline: `2px solid ${theme.colors.border}`, transition: 'none' } }
            : {}),
        };
      });
    }

    return claims;
  }, [hasAccessToAssignClaim, claims, selectedClaimsIds]);

  const hasClearAll = useMemo(
    () => sharedFilters
      ? Boolean(sharedFilters.searchString || hasPaymentType || hasStatus || hasDateRange)
      : Boolean(filtersValues?.[searchKey] || hasPaymentType || hasStatus || hasDateRange),
    [sharedFilters, filtersValues, hasPaymentType, hasDateRange, searchKey, hasStatus]
  );

  const clearAll = useCallback(() => {
    if (sharedFilters && onSharedFiltersChange) {
      onSharedFiltersChange({
        searchString: '',
        paymentType: ALL_OPTION.value as OptionKey,
        statuses: [],
        dateRange: [],
      });
    } else {
      setFilterValue?.[searchKey]?.('');
      setFilterValue?.[paymentTypesKey]?.(ALL_OPTION.value);
      setFilterValue?.[statusesKey]?.([]);
      setFilterValue?.[dateRangeKey]?.([]);
    }
    setInitCleaning(!initCleaning);
  }, [sharedFilters, onSharedFiltersChange, setFilterValue, initCleaning, setInitCleaning, searchKey, paymentTypesKey, statusesKey, dateRangeKey]);

  const history = useHistory();
  const location = useLocation();
  const isEmptyClaimList = useMemo(() => new URLSearchParams(location.search), [location.search])?.get(
    QueryKeys.EMPTY_CLAIMS_LIST
  );

  const headers = useMemo(
    () =>
      [
        ...(hasAccessToAssignClaim
          ? [
              {
                key: 'selectAll',
                title: 'select all claims',
                flex: '0 1 24px',
                style: { justifyContent: 'center', alignItems: 'center' },
                node: (
                  <CheckBoxWrapper>
                    <CheckBox
                      checked={areAllClaimsSelected}
                      onChange={() => {
                        if (areAllClaimsSelected) {
                          selectAllClaims(false);
                          setSelectedClaimsIds([]);
                        } else {
                          selectAllClaims(true);
                          setSelectedClaimsIds(claims.map((claim) => claim.id));
                        }
                      }}
                    />
                  </CheckBoxWrapper>
                ),
              },
            ]
          : []),
        {
          key: 'claimsAssignee',
          title: 'Assignee',
          flex: '0 1 72px',
          style: {
            alignItems: 'center',
            marginRight: '8px',
          },
        },
        {
          key: 'claimsEmployer',
          title: 'ER/SSD',
          flex: 1.1,
        },
        {
          key: 'claimsEmployee',
          title: 'Employee',
          flex: 1.2,
        },
        {
          key: 'claimsClaimID',
          title: 'Claim #',
          flex: 0.8,
        },
        {
          key: 'claimsPaymentType',
          title: 'Type',
          flex: 0.5,
        },
        {
          key: 'claimsAge',
          title: isAppealed && !isShowOnlyEscalated ? 'Appeal Age' : 'Age',
          flex: 0.6,
        },
        ...(isShowOnlyEscalated
          ? [
              {
                key: 'claimsEscalationNote',
                title: 'Escalation note',
                flex: 1.2,
              },
            ]
          : []),
        ...(hasClaimsTeamColumn
          ? [
              {
                key: 'claimsTeam',
                title: 'Claims Team',
                flex: 1.2,
              },
            ]
          : []),
        ...(isShowOnlyEscalated && hasClaimsTeamColumn
          ? []
          : [
              {
                key: 'claimsStatus',
                title: 'Status',
                flex: 1.6,
                style: {
                  marginRight: '20px',
                  marginLeft: '-20px',
                  flexDirection: 'row-reverse',
                },
              },
            ]),
      ] as ListItemBase[],
    [hasAccessToAssignClaim, areAllClaimsSelected, claims, isShowOnlyEscalated, isAppealed, hasClaimsTeamColumn]
  );

  const handlePickOption = (id: string) => history.push(`${routes.CLAIM_PROCESS}/${id}`);
  const handleSelect = useCallback(
    (id: string) => {
      if (selectedClaimsIds.some((item) => item === id)) return;

      setSelectedClaimsIds([...selectedClaimsIds, id]);
    },
    [setSelectedClaimsIds, selectedClaimsIds]
  );

  const placeholder = hasClearAll ? (
    <FlexListPlaceholder />
  ) : (
    <NoSearchItemsPlaceholder
      placeholderTitle="There are no claims yet"
      node={<StyledAbsenceFilesPlaceholderIcon size="72px" />}
    />
  );
  const disabledPeriod = (!hasClearAll && !total) || (!hasDateRange && !total);
  return (
    <Box
      width={globalTheme.defaultContentWidth}
      flex
      direction="column"
      margin={{
        top: `${(hasAccessToAssignClaim ? EXTENDED_HEADER_HEIGHT : HEADER_HEIGHT) + (hasAccessToAssignClaim ? EXTENDED_HEADER_CLAIMS_SPACING : HEADER_CLAIMS_SPACING)}px`,
      }}
    >
      {hasAccessToAssignClaim && selectedClaims.length ? (
        <AssignClaimsProcessorsPopup
          selectedClaims={selectedClaims}
          onClose={() => setSelectedClaimsIds([])}
          isAppeal={isAppealed}
        />
      ) : null}

      <ClearAll onClick={clearAll} isVisible={hasClearAll} />

      <Box background={{ color: 'module' }} round="container1Round">
        <Box align="center" pad={{ bottom: 'spacing16' }}>
          <Box
            width="100%"
            direction="row"
            justify="between"
            align="center"
            pad={{ horizontal: 'spacing24', top: 'spacing24' }}
          >
            <Text weight="bold" color="textBody" style={{ whiteSpace: 'nowrap' }}>
              {isAppealed ? 'Appeals queue' : 'Claims queue'}: {(isEmptyClaimList ? 0 : total) || 0}
            </Text>
            {subTabsNode ? (
              subTabsNode
            ) : (
              !disabled && (
                <Box direction="row" justify="end">
                  <Box margin={{ left: 'spacing8' }} width={{ min: '280px' }}>
                    <SearchInput
                      disabled={(!hasClearAll && !total) || (!searchString && !total)}
                      className="search-input"
                      value={searchString || ''}
                      hasSearchingHistory
                      placeholder="Employer, employee, claim #"
                      onChange={(val: string) => {
                        if (sharedFilters && onSharedFiltersChange) {
                          onSharedFiltersChange({ ...sharedFilters, searchString: val });
                        } else {
                          setFilterValue?.[searchKey]?.(val);
                        }
                        setPage(1);
                      }}
                      resetValue={searchString || initCleaning}
                    />
                  </Box>
                  <Box margin={{ left: 'spacing12' }} width={{ min: '150px', max: '150px' }}>
                    <SelectDropdown
                      disabled={(!hasClearAll && !total) || (!hasPaymentType && !total)}
                      id="paymentType"
                      testId="paymentType"
                      singleMode
                      activeTitle
                      ellipsisMode
                      name="Payment Types"
                      prefix="Type"
                      options={PAYMENT_TYPES}
                      value={paymentTypes}
                      onChange={(val: OptionKey) => {
                        if (sharedFilters && onSharedFiltersChange) {
                          onSharedFiltersChange({ ...sharedFilters, paymentType: val });
                        } else {
                          setFilterValue?.[paymentTypesKey]?.(val);
                        }
                        setPage(1);
                      }}
                    />
                  </Box>
                  <Box margin={{ left: 'spacing12' }} width={{ min: '210px', max: '210px' }}>
                    <SelectDropdown
                      disabled={(!hasClearAll && !total) || (!hasStatus && !total)}
                      id="status"
                      testId="status"
                      allMode
                      activeTitle
                      ellipsisMode
                      name="Activity status"
                      prefix="Status"
                      options={CLAIMS_STATUSES_OPTIONS}
                      values={statusValues}
                      onChangeValues={(status: OptionKey[]) => {
                        if (sharedFilters && onSharedFiltersChange) {
                          onSharedFiltersChange({ ...sharedFilters, statuses: status });
                        } else {
                          setFilterValue?.[statusesKey]?.(status);
                        }
                        setPage(1);
                      }}
                      dropdownStyles={{
                        width: 'fit-content',
                        maxWidth: 'none',
                        left: 'auto',
                      }}
                    />
                  </Box>
                  <Box margin={{ left: 'spacing12' }} width={{ min: 'control' }}>
                    <DateRangeDropdown
                      disabled={disabledPeriod}
                      prefix="Period:"
                      className="period-picker"
                      onChange={(range) => {
                        if (sharedFilters && onSharedFiltersChange) {
                          onSharedFiltersChange({ ...sharedFilters, dateRange: range });
                        } else {
                          setFilterValue?.[dateRangeKey]?.(range);
                        }
                        setPage(1);
                      }}
                      ellipsisMode
                      startDate={dateRange?.[0]}
                      endDate={dateRange?.[1]}
                      icon={<CalendarIcon color={disabledPeriod ? 'iconDisabled' : 'iconPrimary'} />}
                    />
                  </Box>
                </Box>
              )
            )}
          </Box>
          {subTabsNode && !disabled && (
            <Box
              width="100%"
              direction="row"
              pad={{ horizontal: 'spacing24', top: 'spacing12' }}
            >
              <Box flex>
                <SearchInput
                  disabled={(!hasClearAll && !total) || (!searchString && !total)}
                  className="search-input"
                  value={searchString || ''}
                  hasSearchingHistory
                  placeholder="Employer, employee, claim #"
                  onChange={(val: string) => {
                    if (sharedFilters && onSharedFiltersChange) {
                      onSharedFiltersChange({ ...sharedFilters, searchString: val });
                    } else {
                      setFilterValue?.[searchKey]?.(val);
                    }
                    setPage(1);
                  }}
                  resetValue={searchString || initCleaning}
                />
              </Box>
              <Box margin={{ left: 'spacing12' }} width={{ min: '240px', max: '240px' }}>
                <SelectDropdown
                  disabled={(!hasClearAll && !total) || (!hasPaymentType && !total)}
                  id="paymentType"
                  testId="paymentType"
                  singleMode
                  activeTitle
                  ellipsisMode
                  name="Payment Types"
                  prefix="Type"
                  options={PAYMENT_TYPES}
                  value={paymentTypes}
                  onChange={(val: OptionKey) => {
                    if (sharedFilters && onSharedFiltersChange) {
                      onSharedFiltersChange({ ...sharedFilters, paymentType: val });
                    } else {
                      setFilterValue?.[paymentTypesKey]?.(val);
                    }
                    setPage(1);
                  }}
                />
              </Box>
              <Box margin={{ left: 'spacing12' }} width={{ min: '240px', max: '240px' }}>
                <SelectDropdown
                  disabled={(!hasClearAll && !total) || (!hasStatus && !total)}
                  id="status"
                  testId="status"
                  allMode
                  activeTitle
                  ellipsisMode
                  name="Activity status"
                  prefix="Status"
                  options={CLAIMS_STATUSES_OPTIONS}
                  values={statusValues}
                  onChangeValues={(status: OptionKey[]) => {
                    if (sharedFilters && onSharedFiltersChange) {
                      onSharedFiltersChange({ ...sharedFilters, statuses: status });
                    } else {
                      setFilterValue?.[statusesKey]?.(status);
                    }
                    setPage(1);
                  }}
                  dropdownStyles={{
                    width: 'fit-content',
                    maxWidth: 'none',
                    left: 'auto',
                  }}
                />
              </Box>
              <Box margin={{ left: 'spacing12' }} width={{ min: '150px', max: '150px' }}>
                <DateRangeDropdown
                  disabled={disabledPeriod}
                  prefix="Period:"
                  className="period-picker"
                  onChange={(range) => {
                    if (sharedFilters && onSharedFiltersChange) {
                      onSharedFiltersChange({ ...sharedFilters, dateRange: range });
                    } else {
                      setFilterValue?.[dateRangeKey]?.(range);
                    }
                    setPage(1);
                  }}
                  ellipsisMode
                  startDate={dateRange?.[0]}
                  endDate={dateRange?.[1]}
                  icon={<CalendarIcon color={disabledPeriod ? 'iconDisabled' : 'iconPrimary'} />}
                />
              </Box>
            </Box>
          )}
        </Box>
        {!isEmptyClaimList ? (
          <Box style={{ position: 'relative' }}>
            {!isOnlyMyAssignments && isRefetching && !isLoading ? (
              <Box
                style={{ position: 'absolute', zIndex: '1', opacity: '0.5' }}
                width="100%"
                height="100%"
                background="canvas"
                align="center"
                justify="center"
              >
                <Preloader />
              </Box>
            ) : null}

            <FlexList
              testId={listTestId}
              headers={headers}
              rows={actualClaims || []}
              total={isEmptyClaimList ? 0 : total}
              pad={{ top: '0', bottom: 'spacing24', horizontal: 'spacing24' }}
              moreCount={perPage}
              loading={isLoading}
              placeholder={placeholder}
              options={[
                {
                  name: 'Process',
                  onClick: handlePickOption,
                },
                ...(hasAccessToAssignClaim
                  ? [
                      {
                        name: 'Assign',
                        onClick: handleSelect,
                      },
                    ]
                  : []),
              ]}
              footer={
                <Box style={{ position: 'relative' }}>
                  {selectedClaims?.length ? (
                    <Box
                      width="100%"
                      height="100%"
                      style={{ zIndex: '1', position: 'absolute', cursor: 'not-allowed' }}
                    />
                  ) : null}
                  <PaginationBar
                    page={page}
                    total={total}
                    pageSize={perPage}
                    onChangePage={setPage}
                    onChangePageSize={setPerPage}
                  />
                </Box>
              }
            />
          </Box>
        ) : (
          <EmptyClaimsPlaceholder />
        )}
      </Box>
    </Box>
  );
};

export default ClaimsList;
